import { useCallback } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { routeToPageSlug } from "@/features/auth/lib/page-routes";
import { EXPLORE_ROUTES, DEFAULT_EXPLORE_SEARCH, parseExploreSearch, searchFromLocationHref } from "@/features/dashboard/lib/explore-search";

/**
 * Wraps `setActiveTenant` with client-side validation.
 *
 * Before switching, checks whether the target tenant has a PAGE permission
 * for the current route. If not, switches anyway and invalidates the router so
 * the route guard re-runs and renders the unauthorized stub (PageError) in
 * place — no redirect (AD-1622).
 */
export function useTenantSwitch() {
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const switchTenant = useCallback(
    (tenantId: string) => {
      if (tenantId === activeTenantId) return;
      const target = tenants.find((t) => t.id === tenantId);
      if (!target) return;

      const currentSlug = routeToPageSlug(pathname);

      setActiveTenant(tenantId);

      // Unknown route — no permission guard, stay put
      if (currentSlug == null) return;

      // Check if the target tenant grants the current page
      const hasCurrentPage = target.permissions.some(
        (p) => p.type === "PAGE" && p.route === currentSlug,
      );

      if (hasCurrentPage) {
        // Clear the previous tenant's filter params; preserve explore mode.
        if (EXPLORE_ROUTES.has(pathname)) {
          const current = parseExploreSearch(
            searchFromLocationHref(router.state.location.href),
          );
          const search =
            current.mode !== DEFAULT_EXPLORE_SEARCH.mode
              ? { mode: current.mode }
              : {};
          router.navigate({ to: pathname, search: search as never, replace: true });
        }
        return;
      }

      // Current page not available under the new tenant.
      // AD-1622: do NOT redirect. Re-run the route guard so the page renders
      // the unauthorized stub (PageError), which offers a way to navigate away.
      void router.invalidate();
    },
    [tenants, activeTenantId, setActiveTenant, pathname, router],
  );

  return switchTenant;
}
